import { getNestedDynamicFieldPayloadKey, isDynamicFieldNestedInPayload } from '../../../utility/dynamicFieldDestination';
import { isFurnishedYes } from '../../../helpers/furnishedPrefill';
import { shouldSendAreaListingFields } from '../../../utility/postListingPayloadArea';
import {
  assignRootDynamicListingField,
  createFormatDynamicFieldForApi,
  mergePreservedHiddenDynamicFields,
} from './dynamicFieldsPayloadUtils';

const resolveCommonLocationId = (composite, values) => {
  const locationInfo = composite ?? values?.['location-info'];
  // Prefer the selected location (district) over the city; fall back to the city only when no
  // location is picked. Sending the city id here would surface as a numeric id in the edit form,
  // because the city id is never among the city's child-location options.
  return (
    locationInfo?.location?.id ??
    locationInfo?.location?.location_id ??
    locationInfo?.city?.id ??
    locationInfo?.city?.location_id ??
    null
  );
};

const formatDynamicFieldForApi = createFormatDynamicFieldForApi({
  resolveLocationId: resolveCommonLocationId,
  slugPreferredKeys: ['sale_type'],
});

const autoGeneratedListingContentPayload = ({ values, content_type, listingId }) => {
  let purposeName = 'Sale';
  if (values?.purpose_name) {
    purposeName = values.purpose_name;
  } else if (values?.listing_purpose?.title) {
    purposeName = values.listing_purpose.title;
  } else if (values?.purpose === 2) {
    purposeName = 'Rent';
  }

  const resolvedListingId = listingId ?? values?.id;
  return {
    content_type,
    purpose_name: purposeName,
    type: values?.property_type_label,
    city: values?.['location-info']?.city?.title?.en,
    ...(values?.furnished != null &&
      values?.furnished !== '' && { furnished: isFurnishedYes(values?.furnished) ? 'yes' : 'no' }),
    rental_frequency: values?.purpose === 2 && values?.rental_frequency,
    // Rent and sale both use the `price` form field (same field as sell type).
    price: values?.price ? parseInt(values.price) : 0,
    beds: parseInt(values?.bedrooms) || null,
    baths: parseInt(values?.bathrooms) || null,
    ...(resolvedListingId != null && resolvedListingId !== '' && { listing_id: String(resolvedListingId) }),
  };
};

const getUpdateListingPayload = ({ userId, values, listing, dynamicFieldDefinitions = [] }) => {
  const locationId = resolveCommonLocationId(undefined, values);
  const includeAreaListingFields = shouldSendAreaListingFields(dynamicFieldDefinitions);
  // Rent and sale both use the `price` form field (same field as sell type).
  const priceValue = values?.price;
  const price = priceValue != null && priceValue !== '' ? Number(priceValue) : null;

  const dynamicFields = {};
  const rootDynamicFields = {};
  dynamicFieldDefinitions.forEach((def) => {
    const isVisible = def.display === true;
    const isHiddenRequired = def.display !== true && def.is_required === true;
    if (!isVisible && !isHiddenRequired) return;

    let raw;
    if (isVisible) {
      raw = values[def.formKeyName];
    } else if (def.options?.length > 0) {
      const firstOption = def.options[0];
      const isMultiOrFeatures = def.data_type === 'multi-select' || def.apiKeyName === 'features';
      raw = isMultiOrFeatures ? [firstOption] : firstOption;
    } else {
      return;
    }

    const formatted = formatDynamicFieldForApi(
      raw,
      def.data_type,
      def.apiKeyName,
      def.options,
      values,
      def.fieldSlug,
      def.destinationKey,
    );
    if (formatted === undefined) return;
    if (isDynamicFieldNestedInPayload(def.destinationKey)) {
      const nestedKey = getNestedDynamicFieldPayloadKey(def.destinationKey, def.apiKeyName);
      dynamicFields[nestedKey] = formatted;
    } else {
      assignRootDynamicListingField(rootDynamicFields, def.destinationKey, def.apiKeyName, formatted);
    }
  });

  mergePreservedHiddenDynamicFields(
    dynamicFields,
    { values, listing, dynamicFieldDefinitions },
    formatDynamicFieldForApi,
    includeAreaListingFields,
  );

  const listingImagesAttributes = formatDynamicFieldForApi(
    values?.property_images || [],
    'multi-select',
    'images',
    [],
    values,
    'images',
    'listing_images_attributes',
  );

  const listingBody = {
    listing_category_id: values?.property_type ?? null,
    location_id: locationId,
    title: values?.property_title_en ?? '',
    title_l1: values?.property_title_ar ?? '',
    description: values?.property_description_en ?? '',
    description_l1: values?.property_description_ar ?? '',
    ...(price != null && { price }),
    listing_images_attributes: listingImagesAttributes,
    ...(Object.keys(dynamicFields).length > 0 && {
      dynamic_data: { dynamic_fields: dynamicFields },
    }),
  };

  Object.assign(listingBody, rootDynamicFields);

  return {
    listing: listingBody,
    subject_id: userId,
    platform_slugs: ['bayut', 'dubizzle'],
  };
};

export default { autoGeneratedListingContentPayload, getUpdateListingPayload };
